import { useCallback, useMemo } from 'react';

import {
    MutateOptions,
    useMutation,
    UseMutationOptions,
    useQuery,
    useQueryClient,
    QueryResult,
} from '@eika-infrastruktur/react-query';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import type { PaymentItem, PaymentItemDetails, PaymentItemId, PaymentRequest } from '../generated-types';
import { OutboxWrapper } from '../models/outbox';
import {
    createOutboxPayment,
    getOutbox,
    getOutboxDetails,
    removeFromOutbox,
    updateOutboxPayment,
    unapproveOutboxItem,
    UpdateOutboxPaymentRequest,
} from '../services/outboxService';
import { useShareableMutation } from './useCustomMutation';

export interface CreateOutboxPayment {
    paymentRequest: PaymentRequest;
    acceptDuplicate?: boolean;
}

export const OUTBOX_QUERY_KEY = ['outbox'];

export const useInvalidateOutbox = () => {
    const queryClient = useQueryClient();
    return useCallback(() => queryClient.invalidateQueries({ queryKey: OUTBOX_QUERY_KEY }), [queryClient]);
};

export const useOutboxQuery = (enabled = true): QueryResult<OutboxWrapper[]> =>
    useQuery({
        queryKey: OUTBOX_QUERY_KEY,
        queryFn: getOutbox,
        enabled,
    });

export type OutboxPaymentItemResult = {
    items: OutboxWrapper[];
    itemsToIncludeInTotal: OutboxWrapper[];
};

type UseOutboxesResult = {
    data: {
        domesticPaymentsThatNeedSigningFromYou: OutboxPaymentItemResult;
        crossBorderPaymentsThatNeedSigningFromYou: OutboxPaymentItemResult;
        paymentsWaitingForOthersToSign: OutboxPaymentItemResult;
        numberOfPayments: number;
    };
} & Omit<QueryResult<OutboxWrapper[]>, 'data'>;

export const useOutboxes = (enabled = false): UseOutboxesResult => {
    const { data = [], ...query } = useOutboxQuery(enabled);
    const outboxes = useMemo(() => {
        const paymentsToSign = data.filter(
            (outboxItem) => outboxItem.needSigningFromYou || outboxItem.probablyPSD2Payment,
        );
        const paymentsWaitingForOthers = data.filter(
            (outboxItem) => !outboxItem.needSigningFromYou && !outboxItem.probablyPSD2Payment,
        );
        const domesticPayments = paymentsToSign.filter(
            (outboxItem) => outboxItem.paymentItemType !== 'FOREIGN_PAYMENT',
        );
        const crossBorderPayments = paymentsToSign.filter(
            (outboxItem) => outboxItem.paymentItemType === 'FOREIGN_PAYMENT',
        );

        return {
            domesticPaymentsThatNeedSigningFromYou: {
                items: domesticPayments,
                itemsToIncludeInTotal: domesticPayments.filter((payment) => !payment.probablyPSD2Payment),
            },
            crossBorderPaymentsThatNeedSigningFromYou: {
                items: crossBorderPayments,
                itemsToIncludeInTotal: crossBorderPayments.filter((payment) => !payment.probablyPSD2Payment),
            },
            paymentsWaitingForOthersToSign: {
                items: paymentsWaitingForOthers,
                itemsToIncludeInTotal: paymentsWaitingForOthers,
            },
            numberOfPayments: data.length,
        };
    }, [data]);

    return {
        data: outboxes,
        ...query,
    };
};

export const useOutboxDetailsQuery = (paymentId: PaymentItemId | undefined): QueryResult<PaymentItemDetails> =>
    useQuery({
        queryKey: [OUTBOX_QUERY_KEY, paymentId?.value],
        queryFn: () => getOutboxDetails(paymentId!),
        enabled: !!paymentId,
    });

export const useEditOutbox = (
    options: UseMutationOptions<void, RequestExceptionWithResponse, UpdateOutboxPaymentRequest> = {},
) =>
    useMutation({
        ...options,
        mutationKey: ['updateOutbox'],
        mutationFn: updateOutboxPayment,
    });

export const useRemoveOutbox = (options?: MutateOptions<unknown, unknown, Parameters<typeof removeFromOutbox>[0]>) =>
    useMutation({
        ...options,
        mutationKey: ['removeOutbox'],
        mutationFn: removeFromOutbox,
    });

export const useUnapproveOutboxItem = (
    options?: MutateOptions<unknown, unknown, Parameters<typeof unapproveOutboxItem>[0]>,
) =>
    useMutation({
        ...options,
        mutationKey: ['unapproveOutboxItem'],
        mutationFn: unapproveOutboxItem,
    });

export const useCreateOutboxPayment = (
    options?: MutateOptions<PaymentItem | undefined, RequestExceptionWithResponse, CreateOutboxPayment>,
) =>
    useShareableMutation({
        ...options,
        mutationKey: ['create-outbox-payment'],
        mutationFn: (action: CreateOutboxPayment) => createOutboxPayment(action.paymentRequest, action.acceptDuplicate),
    });

export const useMutateOutbox = () => {
    const invalidateOutbox = useInvalidateOutbox();
    return useCreateOutboxPayment({
        onSuccess: invalidateOutbox,
    });
};
